import * as Linking from 'expo-linking';
import * as Notifications from 'expo-notifications';
import { APP_CONTAINER_STACK_ROUTES } from '@/navigators/routes';
import { AppState, AppStateStatus, Platform } from 'react-native';
import { ErrorReporter } from '@/utils/sentry';
import { getActiveRouteName } from '@/utils/navigation';
import { NavigationState } from '@react-navigation/native';
import { useAutoLoginParams } from '@/hooks/use-auto-login-params';
import { useCallback, useEffect, useRef } from 'react';

// Tags Sentry scope with launch entry + first screen, for slicing errors and perf.
// Precedence: punchout/smart_order > push > universal/deep_link > cold_start/web_pageload.
// Warm starts overwrite the tag on every native foreground.
type LaunchEntry =
  | 'cold_start'
  | 'warm_start'
  | 'web_pageload'
  | 'punchout'
  | 'smart_order'
  | 'deep_link'
  | 'universal_link'
  | 'push_notification';

const isBackgroundedState = (state: AppStateStatus) =>
  state === 'background' || state === 'inactive';

export const useSentryLaunchTracking = () => {
  const autoLoginParams = useAutoLoginParams();
  const launchEntryTagSetRef = useRef(false);
  const firstScreenTagSetRef = useRef(false);

  useEffect(() => {
    if (Platform.OS === 'web' || autoLoginParams) {
      return;
    }

    let cancelled = false;
    (async () => {
      const [initialUrl, lastNotificationResponse] = await Promise.all([
        Linking.getInitialURL().catch(() => null),
        Notifications.getLastNotificationResponseAsync().catch(() => null),
      ]);

      if (cancelled) {
        return;
      }

      let asyncEntry: LaunchEntry | null = null;
      if (lastNotificationResponse) {
        asyncEntry = 'push_notification';
      } else if (initialUrl) {
        asyncEntry = initialUrl.startsWith('https://')
          ? 'universal_link'
          : 'deep_link';
      }

      if (asyncEntry) {
        launchEntryTagSetRef.current = true;
        // URL omitted — may contain UUIDs / PII.
        ErrorReporter.setTag('app.launch_entry', asyncEntry);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [autoLoginParams]);

  const handleNavigationReady = useCallback(() => {
    if (launchEntryTagSetRef.current) {
      return;
    }
    launchEntryTagSetRef.current = true;

    // punchout / smart_order are web-only (URL params); native gets cold_start.
    let launchEntry: LaunchEntry;
    if (Platform.OS !== 'web') {
      launchEntry = 'cold_start';
    } else if (!autoLoginParams) {
      launchEntry = 'web_pageload';
    } else {
      launchEntry =
        autoLoginParams.type === 'PUNCHOUT' ? 'punchout' : 'smart_order';
    }

    ErrorReporter.setTag('app.launch_entry', launchEntry);
  }, [autoLoginParams]);

  // Re-tag as warm_start on native foreground. Skipped on web — AppState there
  // fires on tab switches, which would be noise.
  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    const previousStateRef = { current: AppState.currentState };
    const subscription = AppState.addEventListener(
      'change',
      (nextState: AppStateStatus) => {
        const wasBackgrounded = isBackgroundedState(previousStateRef.current);
        previousStateRef.current = nextState;
        if (!wasBackgrounded || nextState !== 'active') {
          return;
        }

        ErrorReporter.setTag('app.launch_entry', 'warm_start');
        ErrorReporter.addBreadcrumb({
          category: 'app.launch',
          level: 'info',
          message: 'warm start',
        });
      },
    );

    return () => {
      subscription.remove();
    };
  }, []);

  // Tag the first non-Container route — onReady fires before auth resolves,
  // so we skip Container until the real stack swaps in.
  const handleNavigationStateChange = useCallback(
    (state: NavigationState | undefined) => {
      if (!state || firstScreenTagSetRef.current) {
        return;
      }
      const routeName = getActiveRouteName(state);
      if (!routeName || routeName === APP_CONTAINER_STACK_ROUTES.Container) {
        return;
      }

      firstScreenTagSetRef.current = true;
      ErrorReporter.setTag('app.first_screen', routeName);
      ErrorReporter.addBreadcrumb({
        category: 'app.launch',
        data: { route: routeName },
        level: 'info',
        message: 'first screen settled',
      });
    },
    [],
  );

  return {
    handleNavigationReady,
    handleNavigationStateChange,
  };
};
